import React, { useState, useRef } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { Sparkles, Upload, FileText, AlertTriangle, X, Loader2, CheckCircle2, XCircle } from "lucide-react";
import TrendChip from "./TrendChip";

const ACCEPTED = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif";
const FLAGS = ["", "High", "Low", "Critical"];
const CONCURRENCY = 3;

function toDateInput(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[\-/](\d{1,2})[\-/](\d{2,4})$/);
  if (m) {
    const [_, d, mo, y] = m;
    const yr = y.length === 2 ? "20" + y : y;
    return `${yr}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return "";
}

const dedupeKey = (r) => `${(r.test_name || "").trim().toLowerCase().replace(/\s+/g, " ")}|${r.test_date || ""}`;

async function extractOne(file) {
  const form = new FormData();
  form.append("file", file);
  const r = await api.post("/ai/extract-inline", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return r.data?.extracted || {};
}

/** Run tasks with a concurrency cap. Calls onProgress(index, patch) as each finishes. */
async function runWithConcurrency(items, limit, worker, onProgress) {
  let cursor = 0;
  const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
    while (true) {
      const i = cursor++;
      if (i >= items.length) return;
      try {
        const result = await worker(items[i], i);
        onProgress(i, { status: "done", extracted: result });
      } catch (e) {
        onProgress(i, { status: "failed", error: e.response?.data?.detail || e.message || "Failed" });
      }
    }
  });
  await Promise.all(runners);
}

export default function AiExtractDialog({ onAdd, testid = "ai-extract", latestMap = {} }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]); // {name, status, error?}
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]); // {test_name, result, unit, reference_range, abnormal_flag, test_date, source, include}
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  const reset = () => { setFiles([]); setRows([]); setBusy(false); };

  const ingest = async (list) => {
    const arr = Array.from(list || []).filter(Boolean);
    if (!arr.length) return;
    setBusy(true);

    // Initialise per-file status
    const initial = arr.map((f) => ({ name: f.name, size: f.size, status: "queued" }));
    setFiles(initial);

    // Track accumulated rows (deduped) across files as they finish
    const accKeys = new Set();
    const accRows = [];

    await runWithConcurrency(
      arr,
      CONCURRENCY,
      async (f, i) => {
        setFiles((prev) => prev.map((x, idx) => idx === i ? { ...x, status: "extracting" } : x));
        const ex = await extractOne(f);
        const date = toDateInput(ex.report_date);
        const lab = ex.laboratory || "";
        const patient = ex.patient_name || "";
        const tests = Array.isArray(ex.tests) ? ex.tests : [];
        const newRows = tests.map((t) => {
          const row = {
            test_name: t.test_name || "",
            result: t.result || "",
            unit: t.unit || "",
            reference_range: t.reference_range || "",
            abnormal_flag: t.abnormal_flag || "",
            test_date: date,
            source: f.name,
            laboratory: lab,
            patient_name: patient,
          };
          const key = dedupeKey(row);
          const dup = accKeys.has(key);
          if (!dup) accKeys.add(key);
          row.include = !dup && !!row.test_name;
          return row;
        });
        accRows.push(...newRows);
        setRows([...accRows]);
        return { count: tests.length };
      },
      (i, patch) => setFiles((prev) => prev.map((x, idx) => idx === i ? { ...x, ...patch } : x)),
    );
    setBusy(false);
    const total = accRows.length;
    if (total === 0) toast.info("Gemini returned no test rows. Try clearer scans.");
    else toast.success(`Extracted ${total} row${total !== 1 ? "s" : ""} from ${arr.length} file${arr.length > 1 ? "s" : ""}`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    ingest(e.dataTransfer.files);
  };

  const updRow = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const toggleAll = (val) => setRows(rows.map((r) => ({ ...r, include: val })));

  const confirm = () => {
    const selected = rows.filter((r) => r.include && r.test_name);
    if (selected.length === 0) { toast.error("Select at least one row with a test name"); return; }
    // Strip helper fields before handing off
    onAdd(selected.map(({ include, source, laboratory, patient_name, ...r }) => r));
    toast.success(`Added ${selected.length} investigation${selected.length > 1 ? "s" : ""}`);
    setOpen(false);
    reset();
  };

  const includedCount = rows.filter((r) => r.include).length;
  const dupCount = rows.filter((r) => !r.include && r.test_name).length;
  const finishedFiles = files.filter((f) => f.status === "done" || f.status === "failed").length;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="gap-1.5 border-pink-300 text-pink-700 hover:bg-pink-50"
        title="Reads a lab PDF or image and drops each test's result, unit and reference range straight into the Investigations table above. Use this when you have a new lab report to enter."
        data-testid={`${testid}-open`}
      >
        <Sparkles size={13} /> AI extract lab values → fills table
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={16} className="text-pink-600" /> AI report extraction
              <span className="text-[10px] font-normal text-slate-500 ml-1">powered by Gemini 3 Pro</span>
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 pr-1 space-y-4">
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2.5 text-amber-900 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong>Verify every value.</strong> AI-extracted values must be checked against the original report before saving. Duplicate rows are pre-unchecked when the same test appears on multiple pages.
              </div>
            </div>

            {files.length === 0 && (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${dragOver ? "border-pink-500 bg-pink-50" : "border-slate-300 hover:border-pink-400 hover:bg-slate-50"}`}
                data-testid={`${testid}-dropzone`}
              >
                <Upload size={28} className="mx-auto text-slate-400 mb-2" />
                <div className="text-sm font-medium text-slate-700">Drop one or more lab reports here or click to browse</div>
                <div className="text-xs text-slate-500 mt-1">PDF · JPG · PNG · WebP · HEIC · up to 20 MB each · batch supported</div>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPTED}
                  multiple
                  onChange={(e) => ingest(e.target.files)}
                  className="hidden"
                  data-testid={`${testid}-file-input`}
                />
              </div>
            )}

            {files.length > 0 && (
              <div className="border border-slate-200 rounded-md" data-testid={`${testid}-file-list`}>
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
                  <span>Files ({finishedFiles}/{files.length} processed)</span>
                  {!busy && (
                    <button onClick={() => inputRef.current?.click()} className="text-pink-700 hover:underline normal-case tracking-normal text-xs" data-testid={`${testid}-add-more`}>+ Add more files</button>
                  )}
                </div>
                <ul className="divide-y divide-slate-100">
                  {files.map((f, i) => (
                    <li key={i} className="px-3 py-2 text-xs flex items-center justify-between" data-testid={`${testid}-file-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={12} className="text-slate-400 shrink-0" />
                        <span className="truncate">{f.name}</span>
                        {f.size ? <span className="text-slate-400 shrink-0">· {(f.size / 1024).toFixed(0)} KB</span> : null}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-3">
                        {f.status === "queued" && <span className="text-slate-500">Queued</span>}
                        {f.status === "extracting" && <><Loader2 size={12} className="animate-spin text-pink-600" /><span className="text-pink-700">Extracting…</span></>}
                        {f.status === "done" && <><CheckCircle2 size={12} className="text-emerald-600" /><span className="text-emerald-700">Done</span></>}
                        {f.status === "failed" && <><XCircle size={12} className="text-red-600" /><span className="text-red-700" title={f.error}>Failed</span></>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {rows.length > 0 && (
              <>
                <div className="flex items-center justify-between text-xs">
                  <div className="text-slate-600">
                    <strong>{rows.length}</strong> row{rows.length !== 1 ? "s" : ""} extracted · <span className="text-emerald-700">{includedCount} selected</span>{dupCount > 0 && <> · <span className="text-amber-700">{dupCount} duplicate{dupCount > 1 ? "s" : ""}</span></>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => toggleAll(true)} className="text-pink-700 hover:underline" data-testid={`${testid}-select-all`}>Select all</button>
                    <span className="text-slate-300">·</span>
                    <button onClick={() => toggleAll(false)} className="text-slate-600 hover:underline" data-testid={`${testid}-select-none`}>Deselect all</button>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="p-2 w-8"></th>
                        <th className="p-2">Test</th>
                        <th className="p-2">Result</th>
                        <th className="p-2">Trend</th>
                        <th className="p-2">Unit</th>
                        <th className="p-2">Ref range</th>
                        <th className="p-2">Flag</th>
                        <th className="p-2">Date</th>
                        <th className="p-2">Source</th>
                        <th className="p-2 w-6"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} className={`border-t border-slate-100 ${r.include ? "" : "opacity-40"}`} data-testid={`${testid}-row-${i}`}>
                          <td className="p-2">
                            <Checkbox checked={r.include} onCheckedChange={(v) => updRow(i, "include", !!v)} data-testid={`${testid}-include-${i}`} />
                          </td>
                          <td className="p-1"><Input value={r.test_name} onChange={(e) => updRow(i, "test_name", e.target.value)} className="h-8 text-sm" data-testid={`${testid}-name-${i}`} /></td>
                          <td className="p-1"><Input value={r.result} onChange={(e) => updRow(i, "result", e.target.value)} className="h-8 text-sm w-20 tabular" data-testid={`${testid}-result-${i}`} /></td>
                          <td className="p-1"><TrendChip testName={r.test_name} value={r.result} latestMap={latestMap} /></td>
                          <td className="p-1"><Input value={r.unit} onChange={(e) => updRow(i, "unit", e.target.value)} className="h-8 text-sm w-16" /></td>
                          <td className="p-1"><Input value={r.reference_range} onChange={(e) => updRow(i, "reference_range", e.target.value)} className="h-8 text-sm w-28" /></td>
                          <td className="p-1">
                            <select value={r.abnormal_flag} onChange={(e) => updRow(i, "abnormal_flag", e.target.value)} className="h-8 text-sm border border-slate-200 rounded px-1 w-20" data-testid={`${testid}-flag-${i}`}>
                              {FLAGS.map((f) => <option key={f || "n"} value={f}>{f || "—"}</option>)}
                            </select>
                          </td>
                          <td className="p-1"><Input type="date" value={r.test_date} onChange={(e) => updRow(i, "test_date", e.target.value)} className="h-8 text-sm w-32 tabular" /></td>
                          <td className="p-1 text-[11px] text-slate-500 truncate max-w-[140px]" title={r.source}>{r.source}</td>
                          <td className="p-1 text-right">
                            <button onClick={() => delRow(i)} className="p-1 text-red-600 hover:bg-red-50 rounded" data-testid={`${testid}-del-${i}`}><X size={14} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2 pt-3 border-t border-slate-200">
            {files.length > 0 && !busy && (
              <Button variant="outline" onClick={() => { reset(); if (inputRef.current) inputRef.current.value = ""; }} data-testid={`${testid}-reset`}>Start over</Button>
            )}
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }} data-testid={`${testid}-cancel`}>Cancel</Button>
            <Button
              disabled={rows.length === 0 || busy || includedCount === 0}
              onClick={confirm}
              className="bg-pink-600 hover:bg-pink-700 text-white gap-1.5"
              data-testid={`${testid}-confirm`}
            >
              <Sparkles size={13} /> Confirm &amp; add {includedCount ? `(${includedCount})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
